import type { QueryClient } from "@tanstack/react-query";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import type { GetTenantResponse } from "@/openapi/generated";
import type { PaginatedResultOfGetLocationResponse } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import type { TenantContentSettingsResult } from "@/lib/utils/content-settings/tenant-content-settings-api";
import type { MediaAssetResult } from "@/lib/utils/media/fetch-media-asset";
import { getMediaAssetQueryKey } from "@/lib/utils/media/media-asset-query-key";
import { PUBLIC_PRESIGNED_URL_STALE_MS } from "@/lib/utils/media/public-media-cache";

const PUBLIC_BUNDLE_STALE_MS = 5 * 60 * 1000;

/** Must use `null` (not `undefined`) so dehydrate JSON matches `useLocationsGet`. */
export const publicLocationsScrollQueryKey = [
  QueryKeys.Locations,
  "scroll",
  null,
] as const;

/**
 * Prefetch already-fetched data so this QueryClient records the same staleTime
 * as the client hooks. Dehydrate does not serialize staleTime; observers apply
 * hook staleTime on mount using dataUpdatedAt.
 */
async function seedFreshQuery<T>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  data: T,
  staleTime: number,
) {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn: () => data,
    staleTime,
  });
}

export async function seedTenantContentSettingsQuery(
  queryClient: QueryClient,
  tenantId: number,
  data: TenantContentSettingsResult,
) {
  // Persist keys as an array so dehydrate JSON round-trips (Set becomes {}).
  await seedFreshQuery(
    queryClient,
    [QueryKeys.TenantContentSettings, tenantId],
    {
      settings: data.settings,
      persistedKeys: [...data.persistedKeys],
    },
    PUBLIC_BUNDLE_STALE_MS,
  );
}

export async function seedPublicTenantQuery(
  queryClient: QueryClient,
  tenantId: number,
  tenant: GetTenantResponse | null | undefined,
) {
  if (!tenant) return;
  await seedFreshQuery(
    queryClient,
    [QueryKeys.Tenant, tenantId, "public"],
    tenant,
    PUBLIC_BUNDLE_STALE_MS,
  );
}

export async function seedTenantLogoPublicQuery(
  queryClient: QueryClient,
  tenantId: number,
  asset: MediaAssetResult,
) {
  if (tenantId <= 0) return;
  await seedFreshQuery(
    queryClient,
    getMediaAssetQueryKey(
      tenantId,
      EntryType.Tenant,
      tenantId,
      MediaGroup.Logo,
      "public",
    ),
    asset,
    PUBLIC_PRESIGNED_URL_STALE_MS,
  );
}

export async function seedPublicLocationsQuery(
  queryClient: QueryClient,
  data: PaginatedResultOfGetLocationResponse,
) {
  await seedFreshQuery(
    queryClient,
    publicLocationsScrollQueryKey,
    data,
    PUBLIC_BUNDLE_STALE_MS,
  );
}

/** Tenant, logo, and CMS copy the public layout needs before its hooks mount. */
export type PublicLayoutSeed = {
  tenantId: number;
  tenant?: GetTenantResponse | null;
  logo?: MediaAssetResult | null;
  contentSettings?: {
    settings: TenantContentSettingsResult["settings"];
    persistedKeys: ReadonlyArray<string>;
  } | null;
};

/**
 * Sync seed for the root QueryClient (created inside `ssr: false`).
 * Navbar/CMS/logo observers mount in the same render as this client, so
 * HydrationBoundary is too late for this layout data.
 */
export function hydratePublicLayoutQueries(
  queryClient: QueryClient,
  { tenantId, tenant, logo, contentSettings }: PublicLayoutSeed,
) {
  if (tenant) {
    queryClient.setQueryData([QueryKeys.Tenant, tenantId, "public"], tenant);
  }
  if (logo && tenantId > 0) {
    queryClient.setQueryData(
      getMediaAssetQueryKey(
        tenantId,
        EntryType.Tenant,
        tenantId,
        MediaGroup.Logo,
        "public",
      ),
      logo,
    );
  }
  if (contentSettings) {
    queryClient.setQueryData([QueryKeys.TenantContentSettings, tenantId], {
      settings: contentSettings.settings,
      persistedKeys: [...contentSettings.persistedKeys],
    });
  }
}
